/**
 * Frame-time instrumentation (allocation-free).
 *
 * Every measurement path here writes into typed arrays that were sized at construction.
 * There is no array growth, no object literal, no string built per frame — the profiler
 * must not be the thing that makes the heap sawtooth it is trying to detect.
 *
 * ## 1 %-low without sorting
 *
 * The 1 %-low figure is the 99th percentile *frame time* over a rolling window. Sorting a
 * window every frame would allocate and cost O(n log n); instead we keep a fixed histogram
 * of frame times (0…`HIST_MAX_MS` in `HIST_BUCKET_MS` buckets) that is incremented when a
 * sample enters the window and decremented when it leaves. The percentile is then a scan
 * over ~2000 counters — cheap, exact to the bucket width, and allocation-free.
 */

/** Timed subsystems. Order is the display order in the F3 overlay. */
export const Channel = {
  Physics: 0,
  Terrain: 1,
  RenderCPU: 2,
  Other: 3,
  /**
   * The audio system's main-thread cost.
   *
   * Its own channel rather than a share of `Other`, because M5's acceptance criterion is a
   * number — "audio adds ≤ 0.5 ms main-thread time" — and a number that is not measured
   * separately is a number nobody can fail. Note what this does *not* include: the synthesis
   * itself runs on the audio thread, so what is timed here is only the per-frame parameter
   * arithmetic and the `AudioParam` writes.
   */
  Audio: 4,
} as const;

export type ChannelId = (typeof Channel)[keyof typeof Channel];

export const CHANNEL_COUNT = 5;
export const CHANNEL_NAMES: readonly string[] = ['physics', 'terrain', 'renderCPU', 'other', 'audio'];

/** Per-frame budgets [ms] from CLAUDE.md, Medium tier at 1080p. */
export const CHANNEL_BUDGET_MS: readonly number[] = [1.0, 2.0, 4.0, 1.0, 0.5];

const HIST_MAX_MS = 200;
const HIST_BUCKET_MS = 0.1;
const HIST_BUCKETS = Math.ceil(HIST_MAX_MS / HIST_BUCKET_MS);

/**
 * Frames needed in the window before {@link Profiler.achievableFps} means anything. Half a
 * second at 120 Hz — enough for a mode to exist, short enough to be available early.
 */
const MIN_SAMPLES_FOR_REFRESH = 60;

/**
 * Sentinel for "this channel is not currently being timed".
 *
 * Deliberately −1 and not NaN. Reading a NaN back out of a Float64Array and handing it to
 * `Number.isNaN` makes V8 materialise it as a boxed HeapNumber — 16 B of garbage per call,
 * which at 120 fps is a steady drip the allocation guard flagged immediately. Timestamps
 * from `performance.now()` are never negative, so −1 is an unambiguous sentinel that stays
 * in unboxed double representation.
 */
const NOT_TIMING = -1;

export interface ProfilerOptions {
  /** Rolling window for fps / 1 %-low statistics [ms]. CLAUDE.md specifies 5 s. */
  windowMs?: number;
  /** Ring capacity. Must cover `windowMs` at the highest expected refresh rate. */
  capacity?: number;
  /** Smoothing half-life for the displayed per-channel millisecond figures [ms]. */
  smoothingHalfLifeMs?: number;
}

export class Profiler {
  readonly windowMs: number;
  readonly capacity: number;

  /** Wall-clock time of the current frame's start [ms]. */
  private frameStartMs = 0;
  private lastFrameStartMs = 0;
  private frameCountValue = 0;
  private hasFrame = false;

  /** Per-channel open-timer starts; {@link NOT_TIMING} when the channel is idle. */
  private readonly channelStart = new Float64Array(CHANNEL_COUNT);
  /** Per-channel accumulated time for the frame in progress [ms]. */
  private readonly channelAccum = new Float64Array(CHANNEL_COUNT);
  /** Per-channel time for the last completed frame [ms]. */
  private readonly channelLast = new Float64Array(CHANNEL_COUNT);
  /** Per-channel exponentially smoothed time [ms] — what the overlay shows. */
  private readonly channelSmoothed = new Float64Array(CHANNEL_COUNT);

  /** Rolling ring of frame durations [ms] and the times they were recorded at. */
  private readonly frameMs: Float32Array;
  private readonly frameAtMs: Float64Array;
  private head = 0; // next write index
  private count = 0; // live samples in the window

  private readonly histogram = new Uint16Array(HIST_BUCKETS);
  private windowSumMs = 0;

  private readonly smoothingHalfLifeMs: number;

  constructor(options: ProfilerOptions = {}) {
    this.windowMs = options.windowMs ?? 5000;
    this.capacity = options.capacity ?? 2048;
    this.smoothingHalfLifeMs = options.smoothingHalfLifeMs ?? 250;
    this.frameMs = new Float32Array(this.capacity);
    this.frameAtMs = new Float64Array(this.capacity);
    this.channelStart.fill(NOT_TIMING);
  }

  /** Call once at the top of the frame, before any channel timing. */
  beginFrame(nowMs: number): void {
    this.frameStartMs = nowMs;
    this.channelAccum.fill(0);
    this.channelStart.fill(NOT_TIMING);
  }

  /** Start timing a channel. Nested `begin` on the same channel is ignored. */
  begin(channel: ChannelId, nowMs: number): void {
    if (this.channelStart[channel] === NOT_TIMING) this.channelStart[channel] = nowMs;
  }

  /**
   * Add an already-measured duration [ms] to a channel's total for this frame.
   *
   * Preferred over `begin`/`end` in the main loop: consecutive sections share one timestamp
   * (the end of physics *is* the start of streaming), which halves the number of
   * `performance.now()` calls per frame and leaves no unattributed gaps between sections.
   */
  addSpan(channel: ChannelId, durationMs: number): void {
    this.channelAccum[channel] += durationMs;
  }

  /** Stop timing a channel and add the elapsed time to this frame's total for it. */
  end(channel: ChannelId, nowMs: number): void {
    const start = this.channelStart[channel];
    if (start === NOT_TIMING) return;
    this.channelAccum[channel] += nowMs - start;
    this.channelStart[channel] = NOT_TIMING;
  }

  /**
   * Call once at the end of the frame, after presenting. Closes out the channel totals and
   * folds the frame duration into the rolling window.
   */
  endFrame(nowMs: number): void {
    for (let c = 0; c < CHANNEL_COUNT; c++) {
      if (this.channelStart[c] !== NOT_TIMING) {
        this.channelAccum[c] += nowMs - this.channelStart[c];
        this.channelStart[c] = NOT_TIMING;
      }
      this.channelLast[c] = this.channelAccum[c];
    }

    if (this.hasFrame) {
      const frameDurationMs = this.frameStartMs - this.lastFrameStartMs;
      if (frameDurationMs > 0) this.pushSample(frameDurationMs, this.frameStartMs);

      // Exponential smoothing of the per-channel figures, frame-rate independent.
      const k = frameDurationMs > 0 ? 1 - Math.pow(2, -frameDurationMs / this.smoothingHalfLifeMs) : 0;
      for (let c = 0; c < CHANNEL_COUNT; c++) {
        this.channelSmoothed[c] += (this.channelLast[c] - this.channelSmoothed[c]) * k;
      }
    } else {
      for (let c = 0; c < CHANNEL_COUNT; c++) this.channelSmoothed[c] = this.channelLast[c];
    }

    this.lastFrameStartMs = this.frameStartMs;
    this.hasFrame = true;
    this.frameCountValue++;
  }

  /** Push a frame duration into the rolling window, evicting anything older than `windowMs`. */
  private pushSample(durationMs: number, atMs: number): void {
    if (this.count === this.capacity) this.evictOldest();
    const i = this.head;
    this.frameMs[i] = durationMs;
    this.frameAtMs[i] = atMs;
    this.histogram[this.bucketOf(durationMs)]++;
    this.windowSumMs += durationMs;
    this.head = (i + 1) % this.capacity;
    this.count++;

    const cutoff = atMs - this.windowMs;
    while (this.count > 0 && this.frameAtMs[this.tailIndex()] < cutoff) this.evictOldest();
  }

  private tailIndex(): number {
    return (this.head - this.count + this.capacity) % this.capacity;
  }

  private evictOldest(): void {
    const i = this.tailIndex();
    const durationMs = this.frameMs[i];
    this.histogram[this.bucketOf(durationMs)]--;
    this.windowSumMs -= durationMs;
    this.count--;
  }

  private bucketOf(durationMs: number): number {
    const b = Math.floor(durationMs / HIST_BUCKET_MS);
    return b < 0 ? 0 : b >= HIST_BUCKETS ? HIST_BUCKETS - 1 : b;
  }

  /** Frames recorded since construction. */
  get frameCount(): number {
    return this.frameCountValue;
  }

  /** Samples currently inside the rolling window. */
  get sampleCount(): number {
    return this.count;
  }

  /** Mean frame time over the window [ms]. */
  get averageFrameMs(): number {
    return this.count === 0 ? 0 : this.windowSumMs / this.count;
  }

  /** Mean fps over the window. */
  get averageFps(): number {
    const ms = this.averageFrameMs;
    return ms > 0 ? 1000 / ms : 0;
  }

  /** Instantaneous fps from the most recent frame. */
  get instantFps(): number {
    const i = (this.head - 1 + this.capacity) % this.capacity;
    const ms = this.count > 0 ? this.frameMs[i] : 0;
    return ms > 0 ? 1000 / ms : 0;
  }

  /**
   * The fps of the slowest `fraction` of frames in the window — "1 %-low" at the default.
   * This is the number the quality governor steers on: averages hide the stutters that
   * actually break the feeling of flight.
   */
  lowFps(fraction = 0.01): number {
    const ms = this.highFrameMs(fraction);
    return ms > 0 ? 1000 / ms : 0;
  }

  /** The frame time at the (1 − `fraction`) percentile [ms]. Allocation-free histogram scan. */
  highFrameMs(fraction = 0.01): number {
    if (this.count === 0) return 0;
    const needed = Math.max(1, Math.ceil(this.count * fraction));
    let seen = 0;
    for (let b = HIST_BUCKETS - 1; b >= 0; b--) {
      seen += this.histogram[b];
      if (seen >= needed) return (b + 1) * HIST_BUCKET_MS;
    }
    return 0;
  }

  /**
   * The most common frame time in the window [ms] — the modal histogram bucket, summed over
   * each bucket and its two neighbours so vsync jitter (which smears one refresh interval
   * across half a dozen 0.1 ms buckets) does not split the cluster and pick a noise spike.
   *
   * Returns 0 until {@link MIN_SAMPLES_FOR_REFRESH} frames are in the window.
   */
  modalFrameMs(): number {
    if (this.count < MIN_SAMPLES_FOR_REFRESH) return 0;
    let bestBucket = -1;
    let bestCount = 0;
    // Bucket 0 and the last bucket have no outside neighbour; both are degenerate cases
    // (a 0 ms frame, or the >= HIST_MAX_MS clamp bucket) and are not plausible modes anyway.
    for (let b = 1; b < HIST_BUCKETS - 1; b++) {
      const clustered = this.histogram[b - 1] + this.histogram[b] + this.histogram[b + 1];
      if (clustered > bestCount) {
        bestCount = clustered;
        bestBucket = b;
      }
    }
    return bestBucket < 0 ? 0 : (bestBucket + 0.5) * HIST_BUCKET_MS;
  }

  /**
   * Best sustained frame rate this machine is actually delivering [Hz]; 0 while unknown.
   *
   * This is the *achievable* ceiling, not a target: on a vsync-locked display it is the panel
   * refresh rate (frames cannot arrive faster than the compositor hands them over), and on a
   * GPU-bound scene it is whatever the GPU manages. The quality governor needs it because the
   * spec's "step up above 80 fps" rule is unreachable by construction on a 60 Hz panel — see
   * `governor.ts`. Derived from the modal frame time rather than the fastest frame, so one
   * spuriously short frame cannot latch the estimate high for the rest of the session.
   */
  get achievableFps(): number {
    const ms = this.modalFrameMs();
    return ms > 0 ? 1000 / ms : 0;
  }

  /** Last completed frame's time for a channel [ms]. */
  channelMs(channel: ChannelId): number {
    return this.channelLast[channel];
  }

  /** Smoothed time for a channel [ms] — stable enough to read off the overlay. */
  channelSmoothedMs(channel: ChannelId): number {
    return this.channelSmoothed[channel];
  }

  /** Sum of the smoothed channel times [ms] (instrumented CPU work, not GPU). */
  totalSmoothedMs(): number {
    let sum = 0;
    for (let c = 0; c < CHANNEL_COUNT; c++) sum += this.channelSmoothed[c];
    return sum;
  }

  /** Forget the rolling window (e.g. after a quality change, so stats are not polluted). */
  resetWindow(): void {
    this.head = 0;
    this.count = 0;
    this.windowSumMs = 0;
    this.histogram.fill(0);
    this.hasFrame = false;
  }
}

/**
 * Used JS heap [bytes], or 0 where the browser does not expose it.
 * Chrome-only (`performance.memory`); the overlay labels it as unavailable elsewhere.
 */
export function heapUsedBytes(): number {
  const perf = globalThis.performance as Performance & { memory?: { usedJSHeapSize: number } };
  return perf?.memory?.usedJSHeapSize ?? 0;
}

/**
 * Which loop channel a slice of allocation belongs to.
 *
 * `Channel.Other` is reused for `afterFrame` — overlays, the HUD, the governor — because that is
 * exactly what that channel already means in the timing breakdown, and a byte budget that named
 * its sections differently from the millisecond budget would be a second vocabulary to learn.
 */
export const ALLOCATION_SLOTS = CHANNEL_COUNT + 1;

/**
 * The extra slot: three's `render()` submit, split out from our own `frameUpdate`.
 *
 * The timing breakdown deliberately charges both to `renderCPU`, because splitting them there
 * would cost an extra `performance.now()` on every frame of every session for a number nobody
 * reads. The allocation probe is opt-in and diagnostic, so it can afford the extra boundary — and
 * it is the single most important boundary there is, because it is the line between "garbage we
 * can fix" and "garbage three makes on our behalf".
 */
export const ALLOCATION_SLOT_RENDER_SUBMIT = CHANNEL_COUNT;

/** Names for the allocation slots, in slot order. */
export const ALLOCATION_SLOT_NAMES: readonly string[] = [
  'physics',
  'terrain',
  'frameUpdate (ours)',
  'afterFrame',
  'audio',
  'render() (three)',
];

/**
 * Where the garbage comes from, per loop channel.
 *
 * ## Why this exists
 *
 * Non-negotiable #3 is "zero heap allocations in per-frame hot paths", and until now the only
 * instrument for it was a whole-heap figure: `npm run perf` could say the heap grew, and could not
 * say *which part of the frame grew it*. A milestone was spent attributing a 54 KB/frame
 * allocation rate to "the render path" on the strength of a comment in CLAUDE.md rather than a
 * measurement, which is precisely the sort of confident guess this project has learned to distrust.
 *
 * The loop already takes a timestamp at every channel boundary. Taking a heap reading at the same
 * points costs one more property read per boundary and turns "the frame allocates 54 KB" into "the
 * frame allocates 54 KB, and here is which sixth of it does".
 *
 * ## What the numbers mean, and do not mean
 *
 * Only **positive** deltas are accumulated. A garbage collection landing inside a channel makes its
 * delta negative, and there is no way to tell how much was allocated before the collector ran, so
 * that sample is discarded rather than guessed at. Every figure here is therefore a **lower bound**
 * on what its channel allocates, and {@link discardedSamples} says how often that happened. The
 * *ratios* between channels are the trustworthy part, and the ratios are what attribution needs.
 *
 * `performance.memory` is Chromium-only and quantised. On a browser without it every reading is
 * zero and the probe reports nothing, which is the correct behaviour for a diagnostic.
 *
 * `updateFixed` / `updateFrame`: neither — it is driven by the loop at channel boundaries, and
 * only when explicitly enabled. It is off in every normal session; see `?allocprobe=1`.
 */
export class AllocationProbe {
  private readonly bytes = new Float64Array(ALLOCATION_SLOTS);
  private lastReading = 0;
  private frameCountValue = 0;
  private discarded = 0;

  /** Call at the top of the frame, before any channel runs. */
  beginFrame(): void {
    this.lastReading = heapUsedBytes();
  }

  /** Call at the end of `channel`, having just finished its work. */
  mark(channel: number): void {
    const reading = heapUsedBytes();
    const delta = reading - this.lastReading;
    this.lastReading = reading;
    if (delta > 0) this.bytes[channel] += delta;
    else if (delta < 0) this.discarded++;
  }

  /** Call once the frame's channels have all been marked. */
  endFrame(): void {
    this.frameCountValue++;
  }

  get frames(): number {
    return this.frameCountValue;
  }

  /** Boundaries where a collection ran, so the reading was thrown away. */
  get discardedSamples(): number {
    return this.discarded;
  }

  /** Lower bound on bytes this channel allocates per frame. */
  bytesPerFrame(channel: number): number {
    return this.frameCountValue === 0 ? 0 : this.bytes[channel] / this.frameCountValue;
  }

  /** Lower bound on bytes the whole frame allocates. */
  totalBytesPerFrame(): number {
    let total = 0;
    for (let i = 0; i < ALLOCATION_SLOTS; i++) total += this.bytes[i];
    return this.frameCountValue === 0 ? 0 : total / this.frameCountValue;
  }

  reset(): void {
    this.bytes.fill(0);
    this.frameCountValue = 0;
    this.discarded = 0;
    this.lastReading = heapUsedBytes();
  }
}
